<template>
  <div v-loading="loading" class="p-0" style="max-width: 900px">
    <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
      <div class="flex items-center gap-x-3 flex-wrap">
        <el-button size="mini" icon="el-icon-arrow-left" @click="$router.push({ name: 'warranties' })">
          К списку
        </el-button>
        <h1 class="text-xl font-semibold">Заявка #{{ id }}</h1>
        <el-tag v-if="warranty" size="mini" :type="statusType(warranty.status)" effect="plain">
          {{ statusLabel(warranty.status) }}
        </el-tag>
      </div>
      <div v-if="warranty" class="text-gray-500 text-sm">
        Заявка от {{ formatDate(warranty.created_at) }}
      </div>
    </div>

    <template v-if="warranty">
      <el-alert
        v-if="purchaseDateTooOld"
        type="warning"
        :closable="false"
        show-icon
        title="Дата покупки старше 3 лет — после активации гарантия сразу будет истёкшей"
        class="mb-4"
      ></el-alert>

      <el-card class="mb-4">
        <div slot="header">Данные заявки</div>
        <el-form :label-position="labelPosition" label-width="160px" size="small">
          <el-form-item label="Клиент">
            <el-input v-model="form.client_name" :disabled="isClosed"></el-input>
          </el-form-item>
          <el-form-item label="Телефон (WhatsApp)">
            <el-input v-model="form.phone" :disabled="isClosed"></el-input>
            <a
              class="text-xs text-green-700"
              :href="`https://wa.me/${(form.phone || '').replace(/\D/g, '')}`"
              target="_blank"
              rel="noopener"
            >Написать в WhatsApp</a>
          </el-form-item>
          <el-form-item label="Модель">
            <el-select v-model="form.model" :disabled="isClosed">
              <el-option v-for="model in models" :key="model" :label="model" :value="model"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="Серийный номер">
            <el-input v-model="form.serial_number" :disabled="isClosed"></el-input>
          </el-form-item>
          <el-form-item label="Дата покупки">
            <el-date-picker
              v-model="form.purchase_date"
              type="date"
              value-format="yyyy-MM-dd"
              placeholder="Дата покупки"
              :disabled="isClosed"
            ></el-date-picker>
          </el-form-item>
          <el-form-item label="Заметка мастера">
            <el-input v-model="form.master_note" type="textarea" :rows="2" resize="none" :disabled="isClosed"></el-input>
          </el-form-item>
          <el-form-item>
            <el-button
              v-if="!isClosed"
              type="primary"
              size="small"
              :loading="saving"
              @click="save"
            >Сохранить</el-button>
            <span v-else class="text-gray-500">{{ closedNote }}</span>
            <span v-if="warranty.warranty_until" class="ml-3 text-gray-600">
              Действует до {{ formatDay(warranty.warranty_until) }}
            </span>
            <span v-if="warranty.activated_at" class="ml-3 text-gray-600">
              Активирована {{ formatDate(warranty.activated_at) }}
            </span>
            <span v-if="warranty.activated_by" class="ml-3 text-gray-500 text-xs">
              мастер {{ warranty.activated_by }}
            </span>
          </el-form-item>
        </el-form>
      </el-card>

      <el-card class="mb-4">
        <div slot="header">Фото от клиента</div>
        <div v-if="clientFiles.length === 0" class="text-gray-500">Клиент не приложил файлы</div>
        <div class="flex flex-wrap gap-3">
          <div v-for="file in clientFiles" :key="file.id" class="w-24">
            <el-image
              v-if="isImage(file)"
              :src="fileUrl(file.url)"
              :preview-src-list="previewList(clientFiles)"
              :z-index="3000"
              fit="cover"
              class="w-24 h-24 rounded border border-solid border-gray-200 cursor-pointer"
            ></el-image>
            <a
              v-else
              class="w-24 h-24 rounded border border-solid border-gray-200 flex items-center justify-center text-gray-500"
              :href="fileUrl(file.url)"
              target="_blank"
              rel="noopener"
            ><i class="el-icon-document text-2xl"></i></a>
            <div class="text-xs text-gray-500 truncate" :title="file.title">{{ file.title || "Файл" }}</div>
          </div>
        </div>
      </el-card>

      <el-card class="mb-4">
        <div slot="header">Фото мастера</div>
        <div class="text-gray-500 text-xs mb-2">
          Фото мастера видны только в CRM — клиенту они не показываются.
          <template v-if="!isClosed">Приложить их можно до решения по заявке.</template>
        </div>
        <div v-if="masterFiles.length === 0" class="text-gray-500 mb-2">
          Фото не прикреплены — без них активировать нельзя
        </div>
        <div class="flex flex-wrap gap-3 mb-2">
          <div v-for="file in masterFiles" :key="file.id" class="w-24">
            <el-image
              v-if="isImage(file)"
              :src="fileUrl(file.url)"
              :preview-src-list="previewList(masterFiles)"
              :z-index="3000"
              fit="cover"
              class="w-24 h-24 rounded border border-solid border-gray-200 cursor-pointer"
            ></el-image>
            <a
              v-else
              class="w-24 h-24 rounded border border-solid border-gray-200 flex items-center justify-center text-gray-500"
              :href="fileUrl(file.url)"
              target="_blank"
              rel="noopener"
            ><i class="el-icon-document text-2xl"></i></a>
            <div class="text-xs text-gray-500 truncate" :title="file.title">{{ file.title || "Файл" }}</div>
          </div>
        </div>
        <el-upload
          v-if="!isClosed"
          action=""
          :show-file-list="false"
          :http-request="uploadFile"
          accept="image/*,.pdf,.heic"
          multiple
        >
          <el-button size="small" plain :loading="uploading">Добавить фото</el-button>
        </el-upload>
      </el-card>

      <el-card v-if="isRejected" class="mb-4">
        <div slot="header">Заявка отклонена</div>
        <div class="text-gray-600">Причина отказа: {{ warranty.reject_reason || "не указана" }}</div>
      </el-card>

      <el-card v-if="!isClosed">
        <div slot="header">Решение мастера</div>
        <div class="flex flex-col md:flex-row md:items-center gap-2 md:gap-x-3">
          <el-button
            class="w-full md:w-auto m-0"
            type="success"
            size="small"
            :loading="activating"
            :disabled="masterFiles.length === 0"
            @click="activate"
          >Активировать гарантию на 3 года</el-button>
          <el-button
            class="w-full md:w-auto m-0"
            type="danger"
            size="small"
            plain
            @click="rejectDialog = true"
          >Отклонить</el-button>
        </div>
      </el-card>

      <el-card v-if="isClosed && isAdmin">
        <div slot="header">{{ isActivated ? "Снять активацию" : "Вернуть заявку в работу" }}</div>
        <p class="text-gray-600 m-0 mb-3">
          Заявка вернётся в статус «Новая»: срок гарантии и причина отказа очистятся, и мастер
          снова сможет её править. Нужно кодовое слово.
        </p>
        <el-button
          class="w-full md:w-auto m-0"
          type="warning"
          size="small"
          plain
          @click="deactivateDialog = true"
        >{{ isActivated ? "Снять активацию" : "Вернуть в работу" }}</el-button>
      </el-card>

      <el-dialog
        :title="isActivated ? 'Снять активацию' : 'Вернуть заявку в работу'"
        :visible.sync="deactivateDialog"
        :width="isMobile ? '92%' : '420px'"
      >
        <el-input
          v-model="deactivateCode"
          placeholder="Кодовое слово"
          show-password
          @keyup.enter.native="deactivate"
        ></el-input>
        <span slot="footer">
          <el-button size="small" @click="deactivateDialog = false">Отмена</el-button>
          <el-button
            type="warning"
            size="small"
            :loading="deactivating"
            :disabled="!deactivateCode.trim()"
            @click="deactivate"
          >{{ isActivated ? "Снять активацию" : "Вернуть в работу" }}</el-button>
        </span>
      </el-dialog>

      <el-dialog
        title="Отклонить заявку"
        :visible.sync="rejectDialog"
        :width="isMobile ? '92%' : '420px'"
      >
        <el-input
          v-model="rejectReason"
          type="textarea"
          :rows="3"
          resize="none"
          placeholder="Причина отказа — клиенту она не показывается"
        ></el-input>
        <span slot="footer">
          <el-button size="small" @click="rejectDialog = false">Отмена</el-button>
          <el-button
            type="danger"
            size="small"
            :loading="rejecting"
            :disabled="!rejectReason.trim()"
            @click="reject"
          >Отклонить</el-button>
        </span>
      </el-dialog>
    </template>
  </div>
</template>

<script>
import { format } from "date-fns";
import {
  activateWarranty,
  deactivateWarranty,
  getWarranty,
  rejectWarranty,
  updateWarranty,
  uploadWarrantyFile,
} from "@/api/warranty";

export default {
  name: "WarrantyItem",
  data: () => ({
    loading: false,
    saving: false,
    uploadingCount: 0,
    activating: false,
    rejecting: false,
    rejectDialog: false,
    rejectReason: "",
    deactivateDialog: false,
    deactivateCode: "",
    deactivating: false,
    warranty: null,
    files: [],
    windowWidth: window.innerWidth,
    models: ["TH16", "TH20", "TH24", "TH32"],
    form: {
      client_name: "",
      phone: "",
      model: "",
      serial_number: "",
      purchase_date: "",
      master_note: "",
    },
  }),
  computed: {
    id() {
      return this.$route.params.id;
    },
    // Снять активацию может только админ — мастеру эта кнопка не показывается.
    isAdmin() {
      return this.$store.state.user && this.$store.state.user.role === "admin";
    },
    // Активированная гарантия неизменяема: правки и отказ закрыты и в интерфейсе, и на сервере.
    isActivated() {
      return this.warranty && this.warranty.status === "activated";
    },
    // Отклонённая заявка закрыта целиком, включая работу с фото.
    isRejected() {
      return this.warranty && this.warranty.status === "rejected";
    },
    isClosed() {
      return this.isActivated || this.isRejected;
    },
    closedNote() {
      if (this.isActivated) return "Гарантия активирована — данные заявки больше не меняются";
      if (this.isRejected) return "Заявка отклонена — действия по ней закрыты";
      return "";
    },
    // Мастер работает с телефона: на узком экране подписи полей уезжают над полем
    isMobile() {
      return this.windowWidth < 768;
    },
    labelPosition() {
      return this.isMobile ? "top" : "left";
    },
    // Несколько файлов могут грузиться параллельно (multiple) — спиннер живёт,
    // пока не завершится последняя загрузка, а не гаснет по первой
    uploading() {
      return this.uploadingCount > 0;
    },
    clientFiles() {
      return this.files.filter((file) => file.source === "client");
    },
    masterFiles() {
      return this.files.filter((file) => file.source === "master");
    },
    purchaseDateTooOld() {
      if (!this.form.purchase_date) return false;
      const purchase = new Date(this.form.purchase_date);
      const limit = new Date();
      limit.setFullYear(limit.getFullYear() - 3);
      return purchase < limit;
    },
  },
  async created() {
    await this.load();
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    onResize() {
      this.windowWidth = window.innerWidth;
    },
    // resetForm=false используется после файловых операций: warranty/files обновляются,
    // а несохранённый текст в форме (например, заметка мастера) не затирается данными с сервера
    async load({ resetForm = true } = {}) {
      this.loading = true;
      try {
        const data = await getWarranty(this.id);
        if (!data.success) {
          this.$message.error(data.message);
          return;
        }
        this.warranty = data.warranty;
        this.files = data.files;
        if (resetForm) {
          this.form = {
            client_name: data.warranty.client_name,
            phone: data.warranty.phone,
            model: data.warranty.model,
            serial_number: data.warranty.serial_number,
            purchase_date: data.warranty.purchase_date || "",
            master_note: data.warranty.master_note || "",
          };
        }
      } catch (e) {
        this.$message.error(this.errorText(e));
      }
      this.loading = false;
    },
    async save() {
      this.saving = true;
      try {
        const data = await updateWarranty(this.id, this.form);
        if (!data.success) {
          this.$message.error(data.message);
        } else {
          this.$message.success("Сохранено");
          // форма только что успешно отправлена — можно безопасно перечитать её с сервера
          await this.load();
        }
      } catch (e) {
        this.$message.error(this.errorText(e));
      }
      this.saving = false;
    },
    async uploadFile(request) {
      this.uploadingCount += 1;
      try {
        const data = await uploadWarrantyFile(this.id, request.file);
        if (!data.success) this.$message.error(data.message);
        // файловая операция — не трогаем несохранённые правки в форме
        await this.load({ resetForm: false });
      } catch (e) {
        this.$message.error(this.errorText(e));
      }
      this.uploadingCount -= 1;
    },
    // Браузеры не показывают heic и pdf в <img>, для них оставляем ссылку-иконку.
    isImage(file) {
      return /\.(jpe?g|png|webp|gif)$/i.test(file.url || "");
    },
    previewList(files) {
      return files.filter((file) => this.isImage(file)).map((file) => this.fileUrl(file.url));
    },
    async activate() {
      this.activating = true;
      try {
        const data = await activateWarranty(this.id, this.form.purchase_date);
        if (!data.success) {
          this.$message.error(data.message);
        } else {
          this.$message.success("Гарантия активирована");
        }
        // после активации дата покупки могла быть нормализована сервером — обновляем форму
        await this.load();
      } catch (e) {
        this.$message.error(this.errorText(e));
      }
      this.activating = false;
    },
    async deactivate() {
      if (!this.deactivateCode.trim()) return;
      this.deactivating = true;
      try {
        const data = await deactivateWarranty(this.id, this.deactivateCode.trim());
        if (!data.success) {
          this.$message.error(data.message);
        } else {
          this.deactivateDialog = false;
          this.deactivateCode = "";
          this.$message.success("Заявка возвращена в работу");
          await this.load();
        }
      } catch (e) {
        this.$message.error(this.errorText(e));
      }
      this.deactivating = false;
    },
    async reject() {
      this.rejecting = true;
      try {
        const data = await rejectWarranty(this.id, this.rejectReason);
        if (!data.success) {
          this.$message.error(data.message);
        } else {
          this.rejectDialog = false;
          this.rejectReason = "";
        }
        await this.load();
      } catch (e) {
        this.$message.error(this.errorText(e));
      }
      this.rejecting = false;
    },
    fileUrl(url) {
      return `${process.env.VUE_APP_DOMAIN}${url}`;
    },
    // Календарные даты приходят строками ГГГГ-ММ-ДД: пересчитывать их в таймзону
    // браузера нельзя — дата покупки уедет на соседний день, в том числе при сохранении.
    formatDay(value) {
      if (!value) return "";
      const [y, m, d] = value.split("-");
      return `${d}.${m}.${y}`;
    },
    // Моменты времени (created_at, activated_at) приходят unix-таймстемпами (core/encoder.py)
    formatDate(value, mask = "dd.MM.yyyy HH:mm") {
      if (!value) return "";
      const ms = value > 1e10 ? value : value * 1000;
      return format(new Date(ms), mask);
    },
    errorText(error) {
      return (error.response && error.response.data && error.response.data.message) || error.message;
    },
    statusLabel(status) {
      return { new: "Новая", activated: "Активирована", rejected: "Отклонена" }[status] || status;
    },
    statusType(status) {
      return { new: "info", activated: "success", rejected: "danger" }[status] || "";
    },
  },
};
</script>
